/**
 * Tagular.js: Tagular events config for the Beam library.
 *
 */

import beam from '@redventures/beam/dist/beam.min.js';

(() => {
	/* eslint-disable camelcase, no-else-return, no-underscore-dangle */
	const { jQuery: $, cohesion } = window;
	window._Beam = beam;

	/* Formatting helpers. */
	function getBooleanFromString(key) {
		return key ? key === 'true' : false;
	}

	// Gets rid of accents/diacritics in text.
	// Ex. "mañana" will be "manana"
	function normalizeText(text) {
		return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
	}

	// Infer type property based on the element's tag.
	function getTypeFromTag(element = this) {
		switch (element.tagName?.toUpperCase()) {
			case 'A':
				// Anchor tags may wrap other elements, check first child to determine type.
				const elementChild = element.children[0];
				const childTag = elementChild?.tagName?.toUpperCase();

				if (['PICTURE', 'IMG'].includes(childTag)) {
					return 'IMAGE';
				}
				if (
					childTag === 'SVG' ||
					element.className?.includes('icon-')
				) {
					return 'ICON';
				}
				if (element.className?.includes('btn')) {
					return 'BUTTON';
				}
				return 'LINK';
			case 'BUTTON':
				return 'BUTTON';
			case 'INPUT':
				return element.type ? element.type.toUpperCase() : 'INPUT';
			default:
				return element.className?.includes('icon-') ? 'ICON' : 'LINK';
		}
	}

	// Generates uuid
	function uuidv4() {
		/* eslint-disable no-bitwise, no-mixed-operators */
		// https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
			/[xy]/g,
			function (c) {
				const r = (Math.random() * 16) | 0,
					v = c === 'x' ? r : (r & 0x3) | 0x8;
				return v.toString(16);
			}
		);
	}

	/* Helpers to set properties from data-tag attributes. */
	function getText() {
		// Get text from inner span if it exists.
		const innerSpan =
			this.children[0]?.tagName === 'SPAN' ? this.children[0] : false;
		const text = innerSpan?.innerText ?? this.innerText;
		const textTag = this.dataset?.tagText ?? text;

		return normalizeText(textTag);
	}

	function getAutofilled() {
		return getBooleanFromString(this.dataset?.tagAutofilled);
	}

	function getFieldValue() {
		return this.dataset?.tagFieldValue ?? this.value;
	}

	function getFieldName() {
		return this.dataset?.tagFieldName ?? this.name;
	}

	function getFieldLabel() {
		return this.dataset?.tagFieldLabel ?? this.label;
	}

	function getFieldType() {
		return this.dataset?.tagFieldType ?? getTypeFromTag(this);
	}

	function getElementType() {
		return this.dataset?.tagElementType ?? getTypeFromTag(this);
	}

	function getActionOutcome() {
		let outcome = 'CLICK';

		if (this.href) {
			const urlObject = new URL(this.href);
			outcome = urlObject.hostname?.includes('plateapr')
				? 'INTERNALLINK'
				: 'EXTERNALLINK';
		}

		return this.dataset?.tagActionOutcome ?? outcome;
	}

	function getFormFields() {
		let fields = [];
		if (this.tagName === 'FORM' && this.elements) {
			fields = Array.from(this.elements).map((el) => ({
				autofilled: getAutofilled.apply(el),
				fieldId: uuidv4(),
				fieldLabel: el.dataset.tagFieldLabel ?? el.placeholder,
				fieldName: el.dataset.tagFieldName ?? el.name,
				fieldType: getFieldType.apply(el),
				fieldValue: el.dataset.tagFieldValue ?? el.value,
			}));
		}

		return fields;
	}

	function getFormTags() {
		const form = this.closest('form');
		let tags = {};

		if (form) {
			tags = {
				formType: form.dataset?.tagFormType,
				formName: form.dataset?.tagFormName ?? form.name,
				formId: form.dataset?.tagFormId ?? form.id,
			};
		}

		return tags;
	}

	const eventsConfig = {
		// Generic links on the page
		'a:not([data-tag-event]):not([disable-tagular])': {
			'@type': 'redventures.usertracking.v3.ElementClicked',
			webElement: {
				elementType: getTypeFromTag,
				location: 'CONTENT',
				position: 'data.beamUid',
				text: getText,
			},
			actionOutcome: getActionOutcome,
			outboundUrl: 'this.href',
		},
		// ElementClicked
		"[data-tag-event='elementClick']:not([add-view])": {
			'@type': 'redventures.usertracking.v3.ElementClicked',
			webElement: {
				elementType: getElementType,
				location: 'data.tagLocation',
				name: 'data.tagName',
				position: 'data.tagPosition',
				text: getText,
			},
			actionOutcome: getActionOutcome,
			outboundUrl: 'this.href',
			correlationId: 'this.uuid',
		},
		// ElementViewed
		"[data-tag-event='elementView']": {
			data: {
				'@type': 'redventures.usertracking.v3.ElementViewed',
				webElement: {
					elementType: getElementType,
					location: 'data.tagLocation',
					name: 'data.tagName',
					position: 'data.tagPosition',
					text: getText,
				},
			},
			settings: {
				event: 'view',
			},
		},
		// ElementClicked + ElementViewed
		"[data-tag-event='elementClick'][add-view]": [
			{
				'@type': 'redventures.usertracking.v3.ElementClicked',
				webElement: {
					elementType: getElementType,
					location: 'data.tagLocation',
					name: 'data.tagName',
					position: 'data.tagPosition',
					text: getText,
				},
				actionOutcome: getActionOutcome,
				outboundUrl: 'this.href',
				correlationId: 'this.uuid',
			},
			{
				data: {
					'@type': 'redventures.usertracking.v3.ElementViewed',
					webElement: {
						elementType: getElementType,
						location: 'data.tagLocation',
						name: 'data.tagName',
						position: 'data.tagPosition',
						text: getText,
					},
				},
				settings: {
					event: 'view',
				},
			},
		],
		// FormSubmitted
		'[data-tag-event="formSubmit"]': {
			data: {
				'@type': 'redventures.usertracking.v3.FormSubmitted',
				field: getFormFields,
				formContext: getFormTags,
			},
			settings: {
				event: 'submit',
			},
		},
		// FieldInputted
		'[data-tag-event="fieldInput"]': {
			data: {
				'@type': 'redventures.usertracking.v3.FieldInputted',
				userInputField: {
					fieldType: getElementType,
					fieldId: 'this.uuid',
					fieldName: getFieldName,
					fieldValue: getFieldValue,
					fieldLabel: getFieldLabel,
					autofilled: getAutofilled,
				},
				formContext: getFormTags,
			},
			settings: {
				event: 'change',
			},
		},
	}; // eventsConfig

	function start() {
		if (cohesion === undefined) {
			console.log('Cohesion not found'); // eslint-disable-line no-console
			return;
		}

		cohesion('ready', () => {
			if (beam === undefined) {
				console.log('Beam not found'); // eslint-disable-line no-console
				return;
			}

			// Generate uuid for formIds.
			$('[data-tag-form-id="uuid"]').attr('data-tag-form-id', uuidv4());

			// Bind events
			beam.load(eventsConfig);

			if ($ === undefined) {
				console.log('jQuery not found'); // eslint-disable-line no-console
			}
		});
	}

	if (document.readyState !== 'loading') {
		start();
	} else {
		document.addEventListener('DOMContentLoaded', start);
	}
})();
